import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core'
import { mdiBellOutline, mdiBellRingOutline, mdiEyeOutline } from '@mdi/js'
import Icon from '@mdi/react'
import axios from 'axios'
import { Link } from 'react-router-dom'

import config from '../../../config'
import colors from '../../../assets/colors'
import { formatDateTime } from '../../alerts/pages/helpers'
import ActionsMenu from '../../alerts/pages/ActionsMenu'
import CreateEdit from '../../alerts/pages/create-edit'

const textMuted = 'rgba(255, 255, 255, 0.65)'

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: colors.primary,
    color: '#FFFF',
    borderRadius: '20px',
    padding: '20px 24px',
    height: '100%',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '8px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#FFFF',
  },
  viewAll: {
    backgroundColor: '#FFFF',
    color: colors.primary,
    borderRadius: '16px',
    height: '30px',
    padding: '0 14px',
    fontSize: '13px',
    textTransform: 'none',
    transition: 'all .4s ease-in-out',
    '&:hover': {
      backgroundColor: '#FFFF',
      opacity: 0.8,
    },
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 0',
    '&:not(:last-child)': {
      borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
    },
  },
  iconBox: {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  info: {
    flex: 1,
    minWidth: 0,
  },
  itemTitle: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#FFFF',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    transition: 'color .2s ease-in-out',
  },
  itemDate: {
    fontSize: '12px',
    color: textMuted,
  },
  reads: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#FFFF',
    [theme.breakpoints.down('xs')]: {
      fontSize: '13px',
    },
  },
  empty: {
    padding: '24px 0',
    textAlign: 'center',
    fontSize: '14px',
    color: textMuted,
  },
}))

const AlertsCard = () => {
  const classes = useStyles()
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingId, setEditingId] = useState(null)

  const fetchAlerts = () =>
    axios
      .get(`${config.API_URL}/alerts`, { params: { rows: 5, page: 1 } })
      .then(resp => setAlerts(resp.data.data || []))
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false))

  useEffect(() => {
    fetchAlerts()
  }, [])

  const handleCloseEdit = () => {
    setEditingId(null)
    fetchAlerts()
  }

  const renderItem = alert => {
    const { dispatched } = alert

    return (
      <Box key={alert.id} className={classes.item}>
        <Box
          className={classes.iconBox}
          style={{
            backgroundColor: dispatched
              ? 'rgba(58, 168, 115, 0.25)'
              : 'rgba(255, 255, 255, 0.1)',
          }}
        >
          <Icon
            path={dispatched ? mdiBellRingOutline : mdiBellOutline}
            size={0.8}
            color={dispatched ? colors.sucess : textMuted}
          />
        </Box>

        <Box className={classes.info}>
          <Typography className={classes.itemTitle}>{alert.title}</Typography>
          <Typography className={classes.itemDate}>
            {dispatched
              ? `Disparado em ${formatDateTime(alert.dispatched_at)}`
              : `Criado em ${formatDateTime(alert.created_at)}`}
          </Typography>
        </Box>

        {dispatched ? (
          <Tooltip title="Lavanderias que leram / receberam">
            <Box className={classes.reads}>
              <Icon path={mdiEyeOutline} size={0.7} color={textMuted} />
              {`${alert.read_count}/${alert.recipients_count}`}
            </Box>
          </Tooltip>
        ) : (
          <Typography className={classes.itemDate}>Não disparado</Typography>
        )}

        <ActionsMenu
          alert={alert}
          iconColor="#FFFF"
          onEdit={item => setEditingId(String(item.id))}
          onDone={fetchAlerts}
        />
      </Box>
    )
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Typography className={classes.title}>Alertas</Typography>
        <Button component={Link} to="/alerts" className={classes.viewAll}>
          Ver todos
        </Button>
      </Box>

      {loading && (
        <Box className={classes.empty}>
          <CircularProgress size={20} style={{ color: '#FFFF' }} />
        </Box>
      )}

      {!loading && !alerts.length && (
        <Typography className={classes.empty}>
          Nenhum alerta cadastrado
        </Typography>
      )}

      {!loading && alerts.map(renderItem)}

      {editingId && <CreateEdit id={editingId} onClose={handleCloseEdit} />}
    </Box>
  )
}

export default AlertsCard
